import React, { useEffect } from "react";
import { useForm, SubmitHandler } from "react-hook-form";
import supportImg from "../../assets/images/others/host-side-images/supportDrawer.png";
import { ReactComponent as SupportSvgIcon } from "../../assets/images/svgs/supportDrawerSvg.svg";
import useStore from "hooks/useStore";
import useRsvp from "containers/rsvp/useHooks";

interface DrawerProps {
  isOpen: boolean;
  onClose: () => void;
}

interface FormData {
  subject: string;
  message: string;
}

const CustomerSupport: React.FC<DrawerProps> = ({ isOpen, onClose }) => {
  const {
    register,
    handleSubmit,
    formState: { errors },
    reset,
    setValue,
  } = useForm<FormData>();

  const { getUserEmail, getUserName, getUserFullName, getCurrentRole } =
    useStore();
  const userEmail = getUserEmail();
  const userName = getUserName();
  const userFullName = getUserFullName();
  const userRole = getCurrentRole();

  const { contactUsSupport } = useRsvp();

  // Handle subject input - capitalize first letter of each word (Title Case)
  const handleSubjectChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const { value } = event.target;
    const processedValue = value
      .split(" ")
      .map((word: string) =>
        word.length > 0
          ? word.charAt(0).toUpperCase() + word.slice(1).toLowerCase()
          : word
      )
      .join(" ");
    event.target.value = processedValue;
    setValue("subject", processedValue, { shouldValidate: true });
  };

  // Handle message input - capitalize first letter of first word (Sentence case)
  const handleMessageChange = (
    event: React.ChangeEvent<HTMLTextAreaElement>
  ) => {
    const { value } = event.target;
    let processedValue = value;
    if (value.length > 0) {
      // Capitalize first letter of the first word
      const firstChar = value.charAt(0).toUpperCase();
      const restOfText = value.slice(1);
      processedValue = firstChar + restOfText;
    }
    event.target.value = processedValue;
    setValue("message", processedValue, { shouldValidate: true });
  };

  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "auto";
      reset();
    }
    return () => {
      document.body.style.overflow = "auto";
    };
  }, [isOpen, reset]);

  // Submit handler
  const onSubmit: SubmitHandler<FormData> = async (data) => {
    const requestBody = {
      name: userFullName,
      subject: data.subject,
      emailOrUsername: userRole === "CHILD" ? userName : userEmail,
      message: data.message,
    };
    console.log(requestBody)

    try {
      await contactUsSupport(requestBody); // Await the API call
      console.log("Request Body:", requestBody);
      onClose();
    } catch (error) {
      console.error("Support API Error:", error);
    }
  };

  return (
    <div
      className={`fixed inset-0 bg-black bg-opacity-30 transition-opacity duration-300 ${
        isOpen ? "opacity-100" : "opacity-0 pointer-events-none"
      }`}
      style={{ zIndex: 'calc(var(--z-drawer) + 1)' }}
      onClick={onClose}
    >
      <div
        className={`fixed top-0 right-0 h-full w-4/5 sm:w-1/2 md:w-1/3 lg:w-[480px] overflow-y-auto bg-white shadow-lg transition-transform duration-300 ease-in-out transform ${
          isOpen ? "translate-x-0" : "translate-x-full"
        }`}
        onClick={(e) => e.stopPropagation()}
      >
        <button
          onClick={onClose}
          className="absolute top-4 right-4 text-2xl text-black hover:text-black"
        >
          &times;
        </button>
        <div className="flex flex-col">
          <div>
            <img
              src={supportImg}
              alt="Support"
              className="w-full h-[460px] object-cover"
            />
          </div>
          <div className="p-4">
            <span className="flex justify-center mt-2">
              <SupportSvgIcon />
            </span>
            <h2 className="text-xl font-semibold text-black text-center mt-4">
              How can we help you?
            </h2>
            <form onSubmit={handleSubmit(onSubmit)} className="space-y-2 mt-4">
              <div className="flex flex-col gap-1">
                <label className="text-label text-base font-semibold">
                  Subject
                </label>
                <input
                  type="text"
                  className="w-full h-[51px] px-2 rounded-lg border border-inputsBorderColor outline-none"
                  {...register("subject", {
                    required: "Subject is required",
                  })}
                  onChange={handleSubjectChange}
                />
                {errors.subject && (
                  <p className="text-red-500 text-sm">
                    {errors.subject.message}
                  </p>
                )}
              </div>
              <div className="flex flex-col gap-1">
                <label className="text-label text-base font-semibold">
                  Send us a Message
                </label>
                <textarea
                  className="w-full min-h-[70px] max-h-[300px] px-2 py-2 rounded-lg border border-inputsBorderColor outline-none overflow-auto"
                  {...register("message", {
                    required: "Message is required",
                  })}
                  onChange={handleMessageChange}
                />
                {errors.message && (
                  <p className="text-red-500 text-sm">
                    {errors.message.message}
                  </p>
                )}
              </div>
              <div className="flex justify-center ">
                <button
                  type="submit"
                  className="px-4 py-3 bg-primaryBlueBg text-white rounded-full w-1/2 mt-4"
                >
                  Submit
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CustomerSupport;
